import { useState, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { REPAIR_TASKS } from '../../data/repairTasks'
import type { BuildProject, BuildStep } from '../../types'
import { playScanSound } from '../../services/sounds'
import { hapticLight, hapticSuccess } from '../../utils/haptic'

function getTask(id: string): BuildProject | null {
  return REPAIR_TASKS.find((t) => t.id === id) ?? null
}

function RepairVisual({ completedSteps, currentStage }: { completedSteps: BuildStep[]; currentStage?: string }) {
  const emojis = completedSteps.map((s) => s.emoji).filter(Boolean)
  return (
    <div className="repair-visual" aria-hidden>
      <div className="repair-visual-toolbox" data-stage={currentStage ?? 'gather'}>
        {emojis.length > 0 ? (
          <span className="repair-visual-parts">
            {emojis.map((e, i) => (
              <span key={i} className="repair-visual-emoji" style={{ animationDelay: `${i * 0.05}s` }}>{e}</span>
            ))}
          </span>
        ) : (
          <span className="repair-visual-empty">🔧</span>
        )}
      </div>
    </div>
  )
}

export function RepairTaskView() {
  const { taskId } = useParams<{ taskId: string }>()
  const task = taskId ? getTask(taskId) : null
  const [stepIndex, setStepIndex] = useState(0)

  const handleNext = useCallback(() => {
    if (!task) return
    hapticLight()
    playScanSound()
    if (stepIndex < task.steps.length - 1) {
      setStepIndex((i) => i + 1)
    }
  }, [task, stepIndex])

  if (!task) {
    return (
      <div className="page page--repair">
        <div className="empty-state">
          <span className="empty-state-emoji" aria-hidden>🔧</span>
          <h2 className="empty-state-title">Repair not found</h2>
          <Link to="/hardware/repair" className="btn-large btn-primary" style={{ textDecoration: 'none' }}>
            Pick a repair
          </Link>
        </div>
      </div>
    )
  }

  const steps = task.steps
  const currentStep = steps[stepIndex]
  const completedSteps = steps.slice(0, stepIndex)
  const isLastStep = stepIndex === steps.length - 1
  const isFinished = stepIndex >= steps.length
  const currentStage = currentStep?.stage

  if (isFinished && steps.length > 0) {
    hapticSuccess()
    return (
      <div className="page page--repair page--repair-done">
        <div className="repair-done-content">
          <RepairVisual completedSteps={steps.map((s) => ({ ...s }))} currentStage="done" />
          <h1 className="repair-done-title">You fixed it! 🎉</h1>
          <p className="repair-done-name">
            <span className="repair-done-emoji" aria-hidden>{task.emoji}</span>
            {task.name}
          </p>
          <p className="repair-done-message">You did it in the app – try it in real life with a grown-up!</p>
        </div>
        <div className="repair-done-actions">
          <Link to={`/hardware/repair/${task.id}`} className="btn-large btn-primary" style={{ textDecoration: 'none' }}>
            Fix again
          </Link>
          <Link to="/hardware/repair" className="btn-large btn-secondary" style={{ textDecoration: 'none' }}>
            Pick another repair
          </Link>
          <Link to="/hardware" className="btn-large btn-secondary" style={{ textDecoration: 'none' }}>
            Back to Hardware Store
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="page page--repair">
      <header className="repair-header">
        <Link to="/hardware/repair" className="btn-back" aria-label="Back to repairs">←</Link>
        <h1 className="repair-task-name">
          <span aria-hidden>{task.emoji}</span> {task.name}
        </h1>
        <p className="repair-progress" aria-live="polite">
          Step {stepIndex + 1} of {steps.length}
        </p>
      </header>

      <RepairVisual completedSteps={completedSteps} currentStage={currentStage} />

      <section className="repair-step" aria-labelledby="repair-step-label">
        <h2 id="repair-step-label" className="repair-step-label">{currentStep?.label}</h2>
        {currentStep?.instruction && (
          <p className="repair-step-instruction">{currentStep.instruction}</p>
        )}
        <button
          type="button"
          className="btn-large btn-primary repair-step-done"
          onClick={handleNext}
          aria-label={isLastStep ? 'Finish' : 'I did it! Next step'}
        >
          {isLastStep ? "I'm done!" : "I did it!"}
        </button>
      </section>
    </div>
  )
}
